import * as styles from "./SectionArticleOrEventCards.module.scss";

import React, { useMemo } from "react";
import { Button } from "../../../components/common";

import cx from "classnames";
import ArticleOrEventCard from "../../../components/SignPostCards/ArticleOrEventCard";
import { mapPromotedItemToSignpostCard } from "../../../utils/mappers";
import WrappedSection, {
	UnwrappedWrappedSection,
} from "../../WrappedSection/WrappedSection";

import { Accordion, ItemLoader } from "./SectionArticleOrEventCards.Components";
import {
	MAX_ACCORDION,
	MAX_LOADER,
	checkIsFlat,
	getBiggestCardTextCount,
	getLoadOption,
} from "./SectionArticleOrEventCards.utils";

export interface SectionArticleOrEventCardsProps {
	title?: string;
	bodyHtmlText?: string;
	items: DogsTrust.SignPostCardProps[];
	link?: { title: string; uri: string };
	displayOption?: "all" | "accordion" | "loader";
	withoutMargins?: boolean;
	forceFlatCards?: boolean;
	showMoreLimit?: number;
	paginationLimit?: number;
	variant?: DogsTrust.ButtonVariant;
	noWrapper?: boolean;
	hideFromJumpLinks?: boolean;
	jumpLinksTitle?: string;
}

export const getSectionArticleOrEventCardsProps = (
	section: Queries.SectionDataFragment,
): SectionArticleOrEventCardsProps => ({
	title: section.field_front_end_title,
	link: { ...section.field_link },
	items: section.promotedLinks.map(mapPromotedItemToSignpostCard),
	displayOption: getLoadOption(section),
});
export const SectionArticleOrEventCards: React.FC<
	SectionArticleOrEventCardsProps
> = ({
	title,
	bodyHtmlText,
	items,
	link,
	displayOption = "all",
	forceFlatCards = false,
	showMoreLimit = MAX_LOADER,
	variant = "tertiary",
	noWrapper = false,
	hideFromJumpLinks = false,
	jumpLinksTitle,
}) => {
	if (!items?.length) {
		// Adding noise to builds, turn on if needed
		// console.warn(
		//     `Hiding ArticleOrEventCards: (${title}) - No items to display.`
		// );
		return null;
	}
	items.sort((a, b) =>
		a.priority < b.priority ? -1 : a.priority < b.priority ? 1 : 0,
	);

	const Items = useMemo(
		() => () => {
			switch (displayOption) {
				case "accordion":
					return <Accordion items={items} />;
				case "loader":
					return (
						<ItemLoader
							items={items}
							showMoreLimit={showMoreLimit}
							variant={variant}
						/>
					);
				case "all":
					return (
						<div className={styles.cards}>
							{items.map((item, i) => (
								<ArticleOrEventCard
									{...item}
									key={item.title}
									label={item.label}
									isOrphan={forceFlatCards || checkIsFlat(items.length, i + 1)}
									biggestSiblingTextCount={getBiggestCardTextCount(items)}
								/>
							))}
						</div>
					);
			}
		},
		[items, displayOption, showMoreLimit, variant, forceFlatCards],
	);

	const Wrapper = noWrapper ? UnwrappedWrappedSection : WrappedSection;

	return (
		<Wrapper sectionName="SectionArticleOrEventCards">
			<div className={styles.sectionarticlecardsinner}>
				{title && (
					<div className={styles.title}>
						{hideFromJumpLinks ? (
							<h2 data-variation="hideFromJumpLinks" className={styles.heading}>
								{title}
							</h2>
						) : jumpLinksTitle ? (
							<h2
								data-jumplinkstitle={jumpLinksTitle}
								className={styles.heading}
							>
								{title}
							</h2>
						) : (
							<h2 className={styles.heading}>{title}</h2>
						)}
					</div>
				)}
				{bodyHtmlText && (
					<div
						className={styles.bodytext}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
						dangerouslySetInnerHTML={{
							__html: bodyHtmlText,
						}}
					/>
				)}
				<div className={styles.articlecardsection}>
					<Items />
				</div>
				{link?.uri && link?.title && (
					<div className={cx(styles.buttonwrapper)}>
						<Button url={link?.uri} variant={"tertiary"} fullWidth={true}>
							{link?.title}
						</Button>
					</div>
				)}
			</div>
		</Wrapper>
	);
};

export default SectionArticleOrEventCards;
